import React from 'react';
import { SchedulePageProvider } from '@components/SchedulePage/SchedulePageContext/SchedulePageContext';
import MainPageLayoutContainer from '@components/MainPageLayout/presentationalComponents/MainPageLayoutContainer';
import MainPageLayoutHeader from '@components/MainPageLayout/presentationalComponents/MainPageLayoutHeader';
import { Box, createStyles } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MainPageLayoutBody from '@components/MainPageLayout/presentationalComponents/MainPageLayoutBody';
import MainPageLayoutSubsection from '@components/MainPageLayout/presentationalComponents/MainPageLayoutSubsection';
import EventLegend from '@components/UnitCalendarPage/EventLegend/EventLegend';
import {
	BlockPageEventConfig,
	BlockPageStyleConfig,
} from '@components/SchedulePage/BlockPageLegend/BlockPageLegendConfiguration';
import ScheduleColumnsContainer from '@components/SchedulePage/ScheduleColumnsContainer/ScheduleColumnsContainer';
import ScheduleLocationSelection from '@components/SchedulePage/ScheduleLocationSelection/ScheduleLocationSelection';
import ScheduleSurgeonMultiSelect from '@components/SchedulePage/ScheduleSurgeonMultiSelect/ScheduleSurgeonMultiSelect';
import { SchedulingPageHeader } from '@components/SchedulePage/SchedulingPageHeader/SchedulingPageHeader';
import usePageEffects from '@utilities/hooks/usePageEffects/usePageEffects';
import { AuthenticatedBanner } from '@components/AuthenticatedBanner/AuthenticatedBanner';
import BoxWithLoader from '@components/Loaders/BoxWithLoader/BoxWithLoader';
import { GET_BLOCKTIME } from '@store/actions/types';
import { BlockNudgeBanner } from '@components/BlockRelease/BlockNudgeBanner/BlockNudgeBanner';
import useHasPermission from '@utilities/hooks/useHasPermission/useHasPermission';
import { PermittedAction } from '@utilities/PermittedAction';

const useStyles = makeStyles((theme) =>
	createStyles({
		bodyClass: {
			'& > div:first-child': {
				'maxWidth': 'unset',
				'& [data-field="component-container"]': {
					height: 'calc(100vh - 68px)',
					paddingBottom: 0,
					display: 'flex',
				},
			},
		},
		legend: {
			boxShadow: 'none',
			paddingLeft: 0,
		},
		subsection: {
			display: 'flex',
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(3),
			gap: theme.spacing(2),
		},
		bannerSpacing: {
			margin: theme.spacing(3, 5, 1, 5),
			width: '100%'
		},
		loaderWrapper: {
			display: 'flex',
			flex: 'auto 1 1',
			position: 'relative',
			maxWidth: '100%',
		},
		loader: {
			zIndex: 1,
		},
		blockNudgeBanner: {
			margin: theme.spacing(0, 5),
			width: '100%'
		}
	}),
);

const SchedulePage = () => {
	const classes = useStyles();
	usePageEffects('Scheduling Calendar');
	const hasPermission = useHasPermission(PermittedAction.VIEW_NAVIGATION_OPTIONS);

	return (
		<SchedulePageProvider>
			<MainPageLayoutContainer pageName="SchedulePage" bodyClass={classes.bodyClass}>
				<MainPageLayoutHeader>
					<SchedulingPageHeader />
					<Box className={classes.bannerSpacing}>
						<AuthenticatedBanner />
					</Box>
					{hasPermission &&
						<Box className={classes.blockNudgeBanner}>
							<BlockNudgeBanner />
						</Box>}
					<MainPageLayoutSubsection className={classes.subsection}>
						<EventLegend
							className={classes.legend}
							config={BlockPageEventConfig}
							styleConfig={BlockPageStyleConfig}
						/>
					</MainPageLayoutSubsection>
					<MainPageLayoutSubsection
						flex="auto"
						justifyContent="end"
						className={classes.subsection}
					>
						<ScheduleSurgeonMultiSelect />
						<ScheduleLocationSelection />
					</MainPageLayoutSubsection>
				</MainPageLayoutHeader>
				<MainPageLayoutBody>
					<BoxWithLoader label={GET_BLOCKTIME} className={classes.loaderWrapper} loaderProps={{ className: classes.loader }}>
						<ScheduleColumnsContainer />
					</BoxWithLoader>
				</MainPageLayoutBody>
			</MainPageLayoutContainer>
		</SchedulePageProvider>
	);
};

export default SchedulePage;
